import React, { useEffect, useMemo, useState } from "react";
import {
  Modal,
  Form,
  Input,
  Select,
  Upload,
  Button,
  Radio,
  Tooltip,
  DatePicker,
  TimePicker,
  App,
  Alert,
} from "antd";
import {
  UploadOutlined,
  InfoCircleOutlined,
  CloseOutlined,
} from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import theme from "../themes";
import styled from "styled-components";
import Papa from "papaparse";
import * as yup from "yup";
import { ReactComponent as CheckCircle } from "../assets/icons/checkCircle.svg";
import {
  createCampaignThunk,
  getCampaignsThunk,
  getListAudiencesThunk,
  uploadFileThunk,
} from "../store/actions/campaign.action";
import { useAppDispatch, useAppSelector } from "../store";
import { getAgentsThunk } from "../store/actions/agent.action";
import ToastMessage from "../utils/toastMessage";
import { isValidUSPhone } from "../utils/phoneValidation";
import { WarningCircle } from "./icons/warningCircle";
import {
  convertScheduleToUTC,
  getTimezoneOptions,
  TIMEZONE_CONFIG,
  type ScheduleData,
} from "../utils/timezoneUtils";
import { getLinesThunk } from "../store/actions/line.action";
import { ILineResponse, LineStatus } from "../models/line.model";
import { IAgent } from "../models/agent.model";
import dayjs from "dayjs";

const { Option } = Select;

interface CreateCampaignModalProps {
  open: boolean;
  onClose: () => void;
}

const CreateCampaignModal: React.FC<CreateCampaignModalProps> = ({
  open,
  onClose,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { message, modal } = App.useApp();
  const toast = ToastMessage(message);
  const [form] = Form.useForm();

  // Yup validation schema with translations
  const campaignSchema = yup.object().shape({
    campaignName: yup
      .string()
      .required(t("campaignModal.create.validation.campaignNameRequired")),
    phoneNumber: yup
      .string()
      .required(t("campaignModal.create.validation.phoneNumberRequired")),
    agent: yup
      .string()
      .required(t("campaignModal.create.validation.agentRequired")),
    recipientListId: yup
      .string()
      .required(t("campaignModal.create.validation.recipientRequired")),
    uploadedFile: yup.array().when("recipientListId", {
      is: (val: string) => val === "new",
      then: (schema) =>
        schema.min(1, t("campaignModal.create.validation.uploadedFile")),
      otherwise: (schema) => schema.notRequired(),
    }),
  });

  const [file, setFile] = useState<File | null>(null);
  const [validCount, setValidCount] = useState<number | null>(null);
  const [invalidCount, setInvalidCount] = useState<number | null>(null);
  const [fileValidationError, setFileValidationError] = useState<string | null>(
    null
  );
  const [isDragging, setIsDragging] = useState(false);
  const [isMissingPhoneColumn, setIsMissingPhoneColumn] = useState(false);
  const [totalCount, setTotalCount] = useState<number | null>(null);
  const [timing, setTiming] = useState("immediate");
  const [schedule, setSchedule] = useState<ScheduleData>({
    date: null,
    time: null,
    ampm: "AM",
    tz: "EST",
  });
  const dispatch = useAppDispatch();
  const [agents, setAgents] = useState<IAgent[]>([]);
  const { lines } = useAppSelector((state) => state.line);
  const [audiences, setAudiences] = useState<any[]>([]);
  const [recipientSelection, setRecipientSelection] = useState<string | null>(
    null
  );

  const selectedAgentId = Form.useWatch("agent", form) as string | undefined;

  const selectedAgent = useMemo(
    () => agents.find((agent) => agent._id === selectedAgentId),
    [agents, selectedAgentId]
  );

  const agentTemplateVariables = useMemo(() => {
    if (!selectedAgent) return [];

    const headers: string[] = [];
    const seen = new Set<string>();

    const pushHeader = (rawValue: string) => {
      const sanitized = rawValue
        .trim()
        .replace(/^\{+/, "")
        .replace(/\}+$/, "")
        .trim();

      if (!sanitized) return;

      const normalized = sanitized.toLowerCase();
      const header =
        normalized === "phone_number" ? "phone_number" : sanitized;

      if (!seen.has(normalized)) {
        seen.add(normalized);
        headers.push(header);
      }
    };

    pushHeader("phone_number");

    const addVariables = (variables?: Record<string, string>) => {
      if (!variables) return;
      Object.values(variables).forEach((variable) => {
        if (typeof variable !== "string") return;
        pushHeader(variable);
      });
    };

    addVariables(selectedAgent.parameters?.system_variables);
    addVariables(selectedAgent.parameters?.custom_variables);

    return headers;
  }, [selectedAgent]);

  const templateCsvHref = useMemo(() => {
    if (agentTemplateVariables.length === 0) return undefined;
    const headerRow = agentTemplateVariables.join(",");
    return `data:text/csv;charset=utf-8,${encodeURIComponent(
      `${headerRow}\n`
    )}`;
  }, [agentTemplateVariables]);

  const downloadTemplateFilename = useMemo(() => {
    if (!selectedAgent) return "campaign_template.csv";
    const sanitizedName =
      selectedAgent.agent_name?.trim().toLowerCase().replace(/\s+/g, "_") ||
      "campaign";
    return `${sanitizedName}_recipients.csv`;
  }, [selectedAgent]);

  const fetchAudiences = async () => {
    try {
      const res = await dispatch(getListAudiencesThunk()).unwrap();
      const filteredAudiences = (res.data || []).filter((aud: any) => {
        const name = (aud.audience_list_name || "").toLowerCase().trim();
        const containsTestUserFor = name.includes("test user for");
        const endsWithCsv = name.endsWith(".csv");
        return !(containsTestUserFor && !endsWithCsv);
      });
      setAudiences(filteredAudiences);
    } catch (error) {
      console.log("error", error);
    }
  };

  const fetchAgents = async () => {
    try {
      const res = await dispatch(
        getAgentsThunk({
          limit: 999,
          page: 1,
          keyword: "",
          direction: "outbound",
        })
      ).unwrap();
      setAgents(res?.data?.agents ?? []);
    } catch (error) {
      console.log("error", error);
    }
  };

  const fetchLines = async () => {
    try {
      await dispatch(
        getLinesThunk({
          limit: 999,
          page: 1,
          line_number: "",
          status: [LineStatus.ACTIVE, LineStatus.MESSAGE_SERVICE_PENDING],
          sort: {},
        })
      ).unwrap();
    } catch (error) {
      console.log("error", error);
    }
  };

  const handleFileChange = ({ fileList }: any) => {
    if (fileList && fileList.length > 0) {
      const nativeFile = fileList[0].originFileObj;
      setFile(nativeFile);
      setFileValidationError(null);
      setInvalidCount(null);
      setIsMissingPhoneColumn(false);
      setTotalCount(null);

      if (nativeFile) {
        Papa.parse(nativeFile, {
          header: true,
          skipEmptyLines: true,
          complete: (results: any) => {
            const data = results.data;
            // Check if phone_number column exists
            if (data.length > 0) {
              const fields: string[] =
                (results.meta && results.meta.fields) || [];
              if (!fields.includes("phone_number")) {
                setFileValidationError(
                  t("campaignModal.create.phoneNumberColumnMissing")
                );
                setIsMissingPhoneColumn(true);
                setValidCount(null);
                setInvalidCount(null);
                setTotalCount(null);
                return;
              }

              // Count valid and invalid phone numbers
              const validPhoneNumbers = new Set();
              let invalidCount = 0;
              let missingValueCount = 0;
              // Iterate all rows; invalid includes empty and malformed values
              data.forEach((row: any) => {
                const phoneNumber = row.phone_number;
                if (phoneNumber && phoneNumber.trim() !== "") {
                  const trimmedPhone = phoneNumber.trim();
                  if (isValidUSPhone(trimmedPhone)) {
                    validPhoneNumbers.add(trimmedPhone);
                  } else {
                    invalidCount++;
                  }
                } else {
                  missingValueCount++;
                }
              });

              const totalInvalid = invalidCount + missingValueCount;
              const totalRows = data.length;

              setValidCount(validPhoneNumbers.size);
              setInvalidCount(totalInvalid);
              setTotalCount(totalRows);

              // Treat any missing or invalid values as errors
              if (totalInvalid > 0) {
                setFileValidationError(
                  `${totalInvalid}/${totalRows} ${t(
                    "campaignModal.create.invalidPhoneNumbers"
                  )}`
                );
              }
            } else {
              setValidCount(0);
              setInvalidCount(0);
              setTotalCount(0);
            }
          },
          error: () => {
            setValidCount(null);
            setInvalidCount(null);
            setIsMissingPhoneColumn(false);
            setTotalCount(null);
            setFileValidationError(t("common.error"));
          },
        });
      }
    } else {
      setFile(null);
      setValidCount(null);
      setInvalidCount(null);
      setFileValidationError(null);
      setIsMissingPhoneColumn(false);
      setTotalCount(null);
    }
  };

  const performCreate = async () => {
    try {
      // Get form values
      const formValues = await form.validateFields();

      // Prepare data for Yup validation
      const validationData = {
        ...formValues,
        uploadedFile: file ? [file] : [],
      };

      // Validate with Yup
      await campaignSchema.validate(validationData, { abortEarly: false });

      // Validate schedule fields when timing is "schedule"
      if (timing === "schedule" && (!schedule.date || !schedule.time)) {
        toast.error(
          t("campaignModal.create.validation.scheduleDateAndTimeRequired"),
          5
        );
        return;
      }

      // 1. Determine audience_list_id (either existing or upload new)
      let audience_list_id: string;
      if (formValues.recipientListId === "new") {
        if (file) {
          const uploadResult = await dispatch(
            uploadFileThunk({ file, agentId: formValues.agent })
          ).unwrap();
          audience_list_id = uploadResult.data.audience_list_id;
          if (!audience_list_id) {
            toast.error(
              t("campaignModal.create.validation.failedToGetAudienceListId"),
              5
            );
            return;
          }
        } else {
          toast.error(
            t("campaignModal.create.validation.recipientRequired"),
            5
          );
          return;
        }
      } else {
        audience_list_id = formValues.recipientListId;
      }

      // 2. Prepare campaign payload (map form fields to API fields)
      const campaignPayload = {
        campaign_name: formValues.campaignName,
        phone_number: formValues.phoneNumber,
        agent_id: formValues.agent,
        audience_list_id,
        campaign_description: formValues.campaignDescription,
        channel: "call",
        schedule_timing: (timing === "immediate"
          ? "send immediately"
          : timing === "schedule"
          ? "schedule for later"
          : "save for testing") as
          | "send immediately"
          | "schedule for later"
          | "save for testing",
        start_at:
          timing === "schedule" && schedule.date && schedule.time
            ? (() => {
                const dateStr = schedule.date.format("YYYY-MM-DD");
                const timeStr = schedule.time.format("hh:mm");
                const ampm = schedule.ampm;

                // Convert 12-hour to 24-hour format
                let [hours, minutes] = timeStr.split(":");
                let hour = parseInt(hours);

                if (ampm === "PM" && hour !== 12) {
                  hour += 12;
                } else if (ampm === "AM" && hour === 12) {
                  hour = 0;
                }

                const formattedHour = hour.toString().padStart(2, "0");
                return `${dateStr}T${formattedHour}:${minutes}:00`;
              })()
            : undefined,
        time_zone: timing === "schedule" ? schedule.tz : undefined,
        number_of_recipients:
          formValues.recipientListId === "new"
            ? validCount || undefined
            : undefined,
      };

      // 3. Create campaign
      try {
        const createResult = await dispatch(createCampaignThunk(campaignPayload)).unwrap();
        await dispatch(
          getCampaignsThunk({
            limit: 10,
            page: 1,
            keyword: "",
            status: "",
            directionFilter: "",
            sort: { createdAt: -1 },
          })
        ).unwrap();
        toast.success(
          `${formValues.campaignName} ${t(
            "campaignModal.create.validation.campaignCreatedSuccessfully"
          )}`,
          5,
          <CheckCircle width={14} height={14} />
        );
        
        // Navigate to campaign detail page
        const campaignId = createResult?.data?.campaign_id;
        if (campaignId) {
          handleClose();
          navigate(`/outbound/${campaignId}`);
        } else {
          handleClose();
        }
      } catch (error) {
        console.log("error", error);
        return;
      }
    } catch (error) {
      if (error instanceof yup.ValidationError) {
        // Handle Yup validation errors
        const errors = error.inner.reduce((acc, err) => {
          if (err.path) {
            acc[err.path] = err.message;
          }
          return acc;
        }, {} as Record<string, string>);

        const fieldErrors = Object.keys(errors).map((key) => ({
          name: key,
          errors: [errors[key]],
        }));

        form.setFields(fieldErrors);
        toast.error(
          t("campaignModal.create.validation.pleaseFixValidationErrors"),
          5
        );
      } else {
        toast.error(t("campaignModal.create.validation.errorOccurred"), 5);
      }
    }
  };

  const handleCreate = async () => {
    // Check file validation first - show confirm instead of blocking
    const isNewRecipient = recipientSelection === "new";
    if (
      isNewRecipient &&
      (fileValidationError || (invalidCount !== null && invalidCount > 0))
    ) {
      modal.confirm({
        title: t("campaignModal.create.proceedWithFileWarnings"),
        content: (
          <div>
            {fileValidationError ||
              `${invalidCount}/${totalCount} ${t(
                "campaignModal.create.invalidPhoneNumbers"
              )}`}
            <div style={{ marginTop: 8 }}>
              {t("campaignModal.create.fileWarningsDescription")}
            </div>
          </div>
        ),
        okText: t("campaignModal.create.continue"),
        cancelText: t("campaignModal.create.updateFile"),
        centered: true,
        zIndex: 1100,
        onOk: async () => {
          await performCreate();
        },
      });
      return;
    }

    await performCreate();
  };

  const handleClose = () => {
    form.resetFields();
    setFile(null);
    setValidCount(null);
    setInvalidCount(null);
    setFileValidationError(null);
    setIsDragging(false);
    setTiming("immediate");
    setSchedule({ date: null, time: null, ampm: "AM", tz: "EST" });
    setIsMissingPhoneColumn(false);
    setTotalCount(null);
    setRecipientSelection(null);
    onClose();
  };

  useEffect(() => {
    fetchAgents();
    fetchLines();
    fetchAudiences();
  }, []);

  // Set default values for agent and phone number when data is available
  useEffect(() => {
    if (open) {
      const currentAgent = form.getFieldValue("agent");
      const currentPhoneNumber = form.getFieldValue("phoneNumber");

      // Set agent default if available and field is empty
      if (!currentAgent && agents.length > 0 && agents[0]?._id) {
        form.setFieldsValue({ agent: agents[0]._id });
      }

      // Set phone number default if available and field is empty
      if (!currentPhoneNumber && lines.lines.length > 0 && lines.lines[0]?.line_number) {
        form.setFieldsValue({ phoneNumber: lines.lines[0].line_number });
      }
    }
  }, [open, agents, lines.lines, form]);

  return (
    <Modal
      title={
        <div style={{ position: "relative", margin: "0 -24px 0 -24px" }}>
          <div
            style={{
              fontSize: 18,
              fontWeight: 500,
              padding: "4px 0 24px 24px",
            }}
          >
            {t("campaignModal.create.title")}
          </div>
          <div
            style={{
              borderBottom: `1px solid ${theme.colors.grey2}`,
              width: "100%",
              marginBottom: 24,
            }}
          />
        </div>
      }
      closeIcon={
        <CloseOutlined
          style={{
            fontSize: 11,
            fontWeight: 700,
            color: "#000",
          }}
        />
      }
      modalRender={(modal) => <StyledModalWrapper>{modal}</StyledModalWrapper>}
      open={open}
      onCancel={handleClose}
      footer={null}
      width={800}
    >
      <Form
        form={form}
        requiredMark={false}
        labelCol={{
          span: 5,
          style: { fontWeight: 600, fontSize: 16 },
        }}
        wrapperCol={{ span: 19 }}
        labelAlign="left"
        validateTrigger="onChange"
      >
        <Form.Item
          label={t("campaignModal.create.campaignName")}
          name="campaignName"
        >
          <Input
            size="large"
            placeholder={t("campaignModal.create.campaignNamePlaceholder")}
            allowClear
            onChange={() =>
              form.setFields([{ name: "campaignName", errors: [] }])
            }
          />
        </Form.Item>
        <Form.Item
          label={t("campaignModal.create.phoneNumber")}
          name="phoneNumber"
        >
          <Select
            size="large"
            placeholder={t("campaignModal.create.phoneNumberPlaceholder")}
            onChange={() =>
              form.setFields([{ name: "phoneNumber", errors: [] }])
            }
          >
            {lines.lines.map((line: ILineResponse) => (
              <Option key={line._id} value={line.line_number}>
                {line.line_number}
              </Option>
            ))}
          </Select>
        </Form.Item>
        <Form.Item label={t("campaignModal.create.agent")} name="agent">
          <Select
            size="large"
            placeholder={t("campaignModal.create.agentPlaceholder")}
            onChange={() => form.setFields([{ name: "agent", errors: [] }])}
          >
            {agents.map((agent) => (
              <Option key={agent._id} value={agent._id}>
                {agent.agent_name}
              </Option>
            ))}
          </Select>
        </Form.Item>
        <Form.Item
          label={t("campaignModal.create.recipients")}
          name="recipientListId"
        >
          <Select
            size="large"
            placeholder={t("campaignModal.create.recipientPlaceholder")}
            value={recipientSelection ?? undefined}
            onChange={(value) => {
              setRecipientSelection(value);
              form.setFields([{ name: "recipientListId", errors: [] }]);
              if (value !== "new") {
                setFile(null);
                setValidCount(null);
                setInvalidCount(null);
                setFileValidationError(null);
                setIsMissingPhoneColumn(false);
                setTotalCount(null);
              }
              form.setFieldsValue({ recipientListId: value });
            }}
          >
            <Option
              value="new"
              style={{
                fontWeight:
                  recipientSelection === "new" || recipientSelection === null
                    ? 600
                    : undefined,
                backgroundColor:
                  recipientSelection === "new" || recipientSelection === null
                    ? theme.colors.blue2
                    : undefined,
              }}
            >
              {t("campaignModal.create.addNewRecipientList")}
            </Option>
            {audiences.map((aud: any) => (
              <Option key={aud._id} value={aud._id}>
                {aud.audience_list_name || "-"}
              </Option>
            ))}
          </Select>
        </Form.Item>
        {recipientSelection === "new" && (
          <Form.Item name="uploadedFile" wrapperCol={{ offset: 5, span: 19 }}>
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                width: "100%",
                gap: 16,
              }}
            >
              {file && (
                <UploadedFileInfoRow>
                  <CheckCircle />
                  <UploadedFileText>
                    {t("campaignModal.create.fileUploaded")}{" "}
                    <Tooltip title={file.name} placement="top">
                      <UploadedFileName>{file.name}</UploadedFileName>
                    </Tooltip>
                  </UploadedFileText>
                  {!fileValidationError && (
                    <UploadedFileMeta>
                      ({validCount}{" "}
                      {t("campaignModal.create.uniqueRecipientsFound")})
                    </UploadedFileMeta>
                  )}
                </UploadedFileInfoRow>
              )}
              {fileValidationError && (
                <Alert
                  type="warning"
                  message={
                    isMissingPhoneColumn ? (
                      <span>
                        {t("campaignModal.create.phoneNumberColumnMissing")}
                      </span>
                    ) : (
                      <span>
                        <b>
                          {invalidCount}/{totalCount}
                        </b>{" "}
                        {t("campaignModal.create.invalidPhoneNumbers")}
                      </span>
                    )
                  }
                  description={
                    isMissingPhoneColumn
                      ? t(
                          "campaignModal.create.phoneNumberColumnMissingDescription"
                        )
                      : t("campaignModal.create.invalidPhoneNumbersDescription")
                  }
                  showIcon
                  icon={
                    <div style={{ margin: "4px 12px 0 0" }}>
                      <WarningCircle />
                    </div>
                  }
                  closable={!isMissingPhoneColumn}
                  onClose={() => setFileValidationError(null)}
                  style={{ fontSize: 14, marginTop: -8 }}
                />
              )}
              {recipientSelection === "new" && (
                <UploadButtonRow>
                  <Upload
                    name="file"
                    fileList={
                      file
                        ? [{ uid: "1", name: file.name, status: "done" }]
                        : []
                    }
                    beforeUpload={() => false}
                    onChange={(info) => {
                      handleFileChange(info);
                      form.setFields([{ name: "uploadedFile", errors: [] }]);
                    }}
                    accept=".csv"
                    maxCount={1}
                    showUploadList={false}
                  >
                    <Button icon={<UploadOutlined />} size="large">
                      {t("campaignModal.create.uploadFile")}
                    </Button>
                  </Upload>
                  <span style={{ color: theme.colors.grey1, fontSize: 14 }}>
                    {t("campaignModal.create.maxFileSize")}
                  </span>
                </UploadButtonRow>
              )}
              {recipientSelection === "new" && !file && (
                <DragDropArea
                  onDragOver={() => setIsDragging(true)}
                  onDragLeave={() => setIsDragging(false)}
                  onDrop={() => setIsDragging(false)}
                >
                  <Upload.Dragger
                    name="file"
                    fileList={[]}
                    beforeUpload={() => false}
                    onChange={(info) => {
                      handleFileChange(info);
                      form.setFields([{ name: "uploadedFile", errors: [] }]);
                    }}
                    accept=".csv"
                    maxCount={1}
                    style={{
                      borderRadius: 8,
                      background: isDragging
                        ? theme.colors.green2
                        : theme.colors.grey4,
                      borderColor: isDragging
                        ? theme.colors.green1
                        : theme.colors.grey1,
                      borderWidth: 2,
                      transition: "all 0.2s",
                    }}
                    height={120}
                    showUploadList={false}
                  >
                    <div
                      style={{
                        color: isDragging
                          ? theme.colors.green1
                          : theme.colors.grey1,
                        fontSize: 15,
                        textAlign: "center",
                      }}
                    >
                      {t("campaignModal.create.dragDropText")}
                      <br />
                      {t("campaignModal.create.dragDropSubtext")}
                    </div>
                  </Upload.Dragger>
                </DragDropArea>
              )}
            </div>
          </Form.Item>
        )}
        {recipientSelection === "new" && (
          <Form.Item wrapperCol={{ offset: 5, span: 19 }}>
            <div style={{ fontWeight: 500, fontSize: 14, marginBottom: 2 }}>
              {t("campaignModal.create.formatting")}
            </div>
            <div
              style={{
                color: theme.colors.grey1,
                fontSize: 13,
                lineHeight: 1.6,
              }}
            >
              {t("campaignModal.create.formattingDescription")}
              <br />
              {t("campaignModal.create.formattingDescription2")}{" "}
              <a
                href={templateCsvHref}
                download={templateCsvHref ? downloadTemplateFilename : undefined}
                onClick={(e) => {
                  if (!selectedAgent) {
                    e.preventDefault();
                    toast.error(
                      t("campaignModal.create.validation.agentRequired"),
                      5
                    );
                  } else if (!templateCsvHref) {
                    e.preventDefault();
                  }
                }}
                style={{
                  color: theme.colors.blue1,
                  textDecoration: "underline",
                }}
              >
                {t("campaignModal.create.downloadTemplate")}
              </a>
            </div>
          </Form.Item>
        )}
        <Form.Item
          label={t("campaignModal.create.timing")}
          required
          style={{ marginBottom: 0 }}
        >
          <Radio.Group
            value={timing}
            onChange={(e) => setTiming(e.target.value)}
            style={{ display: "flex", gap: 32 }}
          >
            <Radio value="immediate">
              {t("campaignModal.create.sendImmediately")}
            </Radio>
            <Radio value="schedule">
              {t("campaignModal.create.scheduleForLater")}
            </Radio>
            <span style={{ display: "flex", alignItems: "center", gap: 4 }}>
              <Radio value="test">
                {t("campaignModal.create.saveForTesting")}
              </Radio>
              {timing === "test" && (
                <Tooltip
                  title={t("campaignModal.create.saveForTestingTooltip")}
                  placement="right"
                >
                  <InfoCircleOutlined
                    style={{
                      color: theme.colors.grey1,
                      cursor: "pointer",
                      fontSize: 15,
                      marginLeft: 2,
                    }}
                  />
                </Tooltip>
              )}
            </span>
          </Radio.Group>
        </Form.Item>
        {timing === "schedule" && (
          <Form.Item
            label={t("campaignModal.common.schedule")}
            required
            style={{ marginTop: 16, marginBottom: 0 }}
          >
            <div style={{ display: "flex", gap: 8 }}>
              <Select
                size="large"
                value={schedule.tz}
                onChange={(tz) => {
                  setSchedule((s) => ({ ...s, tz }));
                  setSchedule((s) => ({ ...s, date: null }));
                }}
                style={{ width: 80 }}
              >
                {getTimezoneOptions().map((tz) => (
                  <Option key={tz.value} value={tz.value}>
                    {tz.label}
                  </Option>
                ))}
              </Select>
              <DatePicker
                size="large"
                placeholder={t("campaignModal.common.scheduleDatePlaceholder")}
                value={schedule.date}
                onChange={(date) => setSchedule((s) => ({ ...s, date }))}
                disabledDate={(current) => {
                  if (!current) return false;

                  // Normalize current to start of day (remove time component)
                  const currentDate = current.startOf("day");

                  // Get today's date in the selected timezone
                  const timezoneName =
                    TIMEZONE_CONFIG[
                      schedule.tz as keyof typeof TIMEZONE_CONFIG
                    ];
                  const todayInTimezone = timezoneName
                    ? dayjs().tz(timezoneName).startOf("day")
                    : dayjs().startOf("day");

                  // Compare normalized calendar date with today in selected timezone
                  const calendarDateStr = currentDate.format("YYYY-MM-DD");
                  const todayStr = todayInTimezone.format("YYYY-MM-DD");

                  return calendarDateStr < todayStr;
                }}
              />
              <TimePicker
                popupClassName="hide-ampm"
                size="large"
                placeholder={t("campaignModal.common.scheduleTimePlaceholder")}
                format="hh:mm"
                use12Hours
                value={schedule.time}
                onChange={(time) => setSchedule((s) => ({ ...s, time }))}
              />
              <Select
                size="large"
                value={schedule.ampm}
                onChange={(ampm) => setSchedule((s) => ({ ...s, ampm }))}
                style={{ width: 80 }}
              >
                <Option value="AM">AM</Option>
                <Option value="PM">PM</Option>
              </Select>
            </div>
          </Form.Item>
        )}
        <Form.Item
          wrapperCol={{ offset: 8, span: 16 }}
          style={{ marginTop: 32 }}
        >
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 24 }}>
            <FooterButton onClick={handleClose}>
              {t("campaignModal.common.cancel")}
            </FooterButton>
            <FooterButton
              primary
              onClick={handleCreate}
              disabled={
                (recipientSelection === "new" && isMissingPhoneColumn) ||
                !form.getFieldValue("recipientListId")
              }
              style={{
                opacity:
                  (recipientSelection === "new" && isMissingPhoneColumn) ||
                  !form.getFieldValue("recipientListId")
                    ? 0.5
                    : 1,
                cursor:
                  (recipientSelection === "new" && isMissingPhoneColumn) ||
                  !form.getFieldValue("recipientListId")
                    ? "not-allowed"
                    : "pointer",
              }}
            >
              {t("campaignModal.common.create")}
            </FooterButton>
          </div>
        </Form.Item>
      </Form>
    </Modal>
  );
};

const StyledModalWrapper = styled.div`
  .ant-modal-close {
    right: 24px;
    top: 20px;
  }

  .ant-form-item {
    margin-bottom: 16px;
  }

  .ant-form-item-explain-error {
    font-size: 12px;
  }

  .ant-upload button {
    background: ${theme.colors.grey3};
    border-color: ${theme.colors.grey2};
  }
`;

const UploadedFileInfoRow = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  height: 30px;
`;

const UploadedFileText = styled.span`
  font-size: 16px;
  font-weight: 400;
  color: ${theme.colors.grey1};
`;

const UploadedFileName = styled.span`
  color: #000;
`;

const UploadedFileMeta = styled.span`
  color: ${theme.colors.grey1};
  font-size: 16px;
  font-weight: 400;
`;

const UploadButtonRow = styled.div`
  display: flex;
  align-items: center;
  gap: 24px;
`;

const DragDropArea = styled.div``;

const FooterButton = styled.button<{ primary?: boolean; disabled?: boolean }>`
  background: ${({ primary, disabled }) =>
    disabled
      ? theme.colors.grey2
      : primary
      ? theme.colors.green1
      : theme.colors.grey3};
  color: ${({ primary, disabled }) =>
    disabled ? theme.colors.grey1 : primary ? "#fff" : "#000"};
  border: 1px solid
    ${({ primary, disabled }) =>
      disabled
        ? theme.colors.grey2
        : primary
        ? theme.colors.green1
        : theme.colors.grey2};
  border-radius: 8px;
  height: 40px;
  padding: 0 16px;
  font-size: 16px;
  font-weight: 400;
  cursor: ${({ disabled }) => (disabled ? "not-allowed" : "pointer")};
  transition: all 0.2s;

  &:hover {
    background: ${({ primary, disabled }) =>
      disabled
        ? theme.colors.grey2
        : primary
        ? theme.colors.green1
        : theme.colors.grey3};
  }

  &:disabled {
    opacity: 0.5;
  }
`;

export default CreateCampaignModal;
